// Runtime processing pipeline for `.luis` source elements.
// It compiles in DOM order and supports incremental rebuild from first change.
import { cloneCompileContext, createEmptyContext } from './context'
import type { CachedSourceEntry } from './process/types'
import { findLuisSourceElements, freezeLuisSourceRegistration } from './dom'
import { buildSourceEntries } from './process/source-entries'
import { canReuseUnchangedTail, findFirstChangedIndex } from './process/cache-compare'
import { pruneIncrementalCompileCache } from './context-incremental-cache'
import { ensureRuntimeFeatures } from '../features'
import { collectRequestedRuntimeFeatures } from './process/requested-features'
import { processBuildSourceEntry } from './process/compile-entry'
import { pruneWorkerCompileCache } from '../compile-dispatch/dispatch'
import { scheduleWorkerWarmup } from './process/worker-warmup'

let previousBuildCache: CachedSourceEntry[] = []
let activeBuildRevision = 0

// Rebuild all source outputs with incremental compilation from first change index.
export async function rebuildLuisSources(): Promise<void> {
  const buildRevision = activeBuildRevision + 1
  activeBuildRevision = buildRevision
  setActiveBuildRevision(buildRevision)
  const sources = findLuisSourceElements()
  const requestedFeatures = collectRequestedRuntimeFeatures(sources)
  await ensureRuntimeFeatures(requestedFeatures)
  const entries = await buildSourceEntries(sources, previousBuildCache)
  if (!isBuildRevisionCurrent(buildRevision)) {
    return
  } else {
    // Continue rebuild only for current build revision.
  }
  const activeSourceIds = entries.map((entry) => (entry.kind === 'link' ? entry.href : entry.sourceId))
  pruneIncrementalCompileCache(activeSourceIds)
  await pruneWorkerCompileCache(activeSourceIds)
  scheduleWorkerWarmup(entries)
  const firstChangedIndex = findFirstChangedIndex(previousBuildCache, entries)
  // Skip rebuild when source order/content is unchanged.
  if (firstChangedIndex === -1) {
    return
  } else {
    // Continue changed-suffix rebuild flow.
  }

  let startIndex = firstChangedIndex
  let context = createEmptyContext()
  // Reuse cached context snapshot when unchanged prefix exists.
  if (startIndex > 0) {
    const previousPrefix = previousBuildCache[startIndex - 1]
    const currentPrefix = entries[startIndex - 1]
    if (!previousPrefix || previousPrefix._sourceId !== currentPrefix.sourceId) {
      startIndex = 0
    } else {
      // Continue rebuild with cached prefix context.
      context = cloneCompileContext(previousPrefix._contextAfter)
    }
  } else {
    // Keep empty context when rebuild starts from first source.
  }

  // Copy unchanged prefix cache entries as-is.
  const nextCache: CachedSourceEntry[] = previousBuildCache.slice(0, startIndex)

  // Compile changed suffix and refresh cache snapshots in order.
  for (let index = startIndex; index < entries.length; index += 1) {
    if (!isBuildRevisionCurrent(buildRevision)) {
      return
    } else {
      // Continue compile loop for current build revision.
    }
    const entry = entries[index]
    const isCompiled = await processBuildSourceEntry(entry, context, buildRevision)
    const cacheEntry: CachedSourceEntry = {
      _sourceId: entry.sourceId,
      _kind: entry.kind,
      _href: entry.href,
      _rawSource: entry.rawSource,
      _signature: entry.signature,
      _contextAfter: cloneCompileContext(context),
    }
    nextCache.push(cacheEntry)

    // Reuse unchanged tail when context converges and remaining entries are stable.
    if (isCompiled && canReuseUnchangedTail(previousBuildCache, entries, index, cacheEntry._contextAfter)) {
      copyCachedTailEntries(nextCache, previousBuildCache, index + 1)
      break
    } else {
      // Continue compiling changed tail entries when reuse is not possible.
    }
  }

  previousBuildCache = nextCache
  freezeLuisSourceRegistration()
}

// Copy previous cache tail entries when compile context proves they are unchanged.
function copyCachedTailEntries(nextCache: CachedSourceEntry[], previous: CachedSourceEntry[], startIndex: number): void {
  // Append unchanged tail entries in original order.
  for (let index = startIndex; index < previous.length; index += 1) {
    nextCache.push(previous[index])
  }
}

// Check whether async rebuild work still belongs to the latest build cycle.
function isBuildRevisionCurrent(buildRevision: number): boolean {
  // Return true only for the latest rebuild revision.
  return activeBuildRevision === buildRevision
}

// Publish latest build revision for async compile/apply guards.
function setActiveBuildRevision(buildRevision: number): void {
  // Skip global marker writes outside browser runtime.
  if (typeof window === 'undefined') {
    return
  } else {
    // Continue latest-build marker update in browser runtime.
  }

  ;(window as Window & { __LentyStyleActiveBuildRevision?: number }).__LentyStyleActiveBuildRevision = buildRevision
}
